// src/admin/LandingPageAnalytics.js
import React, { useState, useEffect } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../config/firebaseConfig';
import { Link } from 'react-router-dom';
// Aşağıdaki satıra "Button" eklendi:
import { Table, Spinner, Alert, Card, Row, Col, ProgressBar, Form, InputGroup, Button } from 'react-bootstrap';
import { 
    ChartBarIcon, 
    EyeIcon, 
    DocumentTextIcon, 
    ArrowTopRightOnSquareIcon,
    MagnifyingGlassIcon,
    ArrowLeftIcon
} from '@heroicons/react/24/outline';

const LandingPageAnalytics = () => {
    const [pages, setPages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [searchTerm, setSearchTerm] = useState('');

    useEffect(() => {
        fetchAnalytics();
    }, []);

    const fetchAnalytics = async () => {
        setLoading(true);
        try {
            const querySnapshot = await getDocs(collection(db, 'customPages'));
            const pageList = querySnapshot.docs.map(doc => {
                const data = doc.data();
                return {
                    id: doc.id,
                    title: data.title || 'Untitled Page',
                    slug: data.slug || '',
                    status: data.status || 'draft',
                    views: data.viewCount || 0,
                    createdAt: data.createdAt ? data.createdAt.toDate().toLocaleDateString('en-GB') : 'N/A'
                };
            });

            // En çok izlenenden en aza doğru sırala
            pageList.sort((a, b) => b.views - a.views);
            setPages(pageList);
        } catch (err) {
            setError('Failed to pull analytics data from the server.');
        } finally {
            setLoading(false);
        }
    };

    // KPI Hesaplamaları
    const totalViews = pages.reduce((acc, curr) => acc + curr.views, 0);
    const highestView = pages.length > 0 ? pages[0].views : 0;
    const activePagesCount = pages.filter(p => p.status === 'published').length;

    const filteredPages = pages.filter(p => 
        p.title.toLowerCase().includes(searchTerm.toLowerCase()) || 
        p.slug.toLowerCase().includes(searchTerm.toLowerCase())
    );

    if (loading) return <div className="text-center mt-5 pt-5"><Spinner animation="border" variant="primary" /></div>;

    return (
        <div className="container-fluid p-4 bg-light min-vh-100">
            {/* Header */}
            <div className="d-flex justify-content-between align-items-center mb-4 pb-2 border-bottom">
                <div>
                    <div className="d-flex align-items-center gap-2">
                        <Link to="/admin/manage-landing-pages" className="text-decoration-none text-muted">
                            <ArrowLeftIcon style={{ width: '18px' }} />
                        </Link>
                        <h1 className="h3 fw-bold mb-0 text-dark">Landing Pages Analytics</h1>
                    </div>
                    <p className="text-muted small mb-0 mt-1">Real-time traffic inspection for dynamically generated canvas pages.</p>
                </div>
                <Button variant="outline-primary" size="sm" onClick={fetchAnalytics} className="rounded-pill px-3">
                    Refresh Data
                </Button>
            </div>

            {error && <Alert variant="danger">{error}</Alert>}

            {/* Top KPI Cards */}
            <Row className="g-3 mb-4">
                <Col md={4}>
                    <Card className="border-0 shadow-sm rounded-4 p-3 bg-white border-start border-primary border-4">
                        <div className="d-flex justify-content-between align-items-center">
                            <div>
                                <span className="text-uppercase text-muted small fw-bold">Total Impressions</span>
                                <h2 className="fw-bolder mb-0 mt-1 text-dark">{totalViews.toLocaleString()}</h2>
                            </div>
                            <div className="bg-primary bg-opacity-10 p-3 rounded-circle text-primary">
                                <EyeIcon style={{ width: '28px' }} />
                            </div>
                        </div>
                    </Card>
                </Col>
                <Col md={4}>
                    <Card className="border-0 shadow-sm rounded-4 p-3 bg-white border-start border-success border-4">
                        <div className="d-flex justify-content-between align-items-center">
                            <div>
                                <span className="text-uppercase text-muted small fw-bold">Active Landings</span>
                                <h2 className="fw-bolder mb-0 mt-1 text-dark">{activePagesCount} <span className="text-muted fs-6 fw-normal">/ {pages.length}</span></h2>
                            </div>
                            <div className="bg-success bg-opacity-10 p-3 rounded-circle text-success">
                                <DocumentTextIcon style={{ width: '28px' }} />
                            </div>
                        </div>
                    </Card>
                </Col>
                <Col md={4}>
                    <Card className="border-0 shadow-sm rounded-4 p-3 bg-white border-start border-warning border-4">
                        <div className="d-flex justify-content-between align-items-center">
                            <div>
                                <span className="text-uppercase text-muted small fw-bold">Top Performing Page</span>
                                <h5 className="fw-bold mb-0 mt-2 text-truncate text-dark" style={{ maxWidth: '180px' }}>
                                    {pages.length > 0 && pages[0].views > 0 ? pages[0].title : 'None yet'}
                                </h5>
                            </div>
                            <div className="bg-warning bg-opacity-10 p-3 rounded-circle text-warning">
                                <ChartBarIcon style={{ width: '28px' }} />
                            </div>
                        </div>
                    </Card>
                </Col>
            </Row>

            {/* Table Section */}
            <Card className="border-0 shadow-sm rounded-4 bg-white">
                <Card.Header className="bg-white border-bottom pt-4 pb-3 px-4 d-flex justify-content-between align-items-center flex-wrap gap-3">
                    <h5 className="fw-bold m-0">Traffic Breakdown</h5>
                    <InputGroup style={{ maxWidth: '300px' }}>
                        <InputGroup.Text className="bg-light border-end-0"><MagnifyingGlassIcon style={{ width: '16px' }}/></InputGroup.Text>
                        <Form.Control 
                            size="sm" 
                            className="bg-light border-start-0 ps-0" 
                            placeholder="Filter by title or slug..." 
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </InputGroup>
                </Card.Header>
                <Card.Body className="p-0">
                    <div className="table-responsive">
                        <Table hover className="align-middle mb-0">
                            <thead className="bg-light text-muted small text-uppercase">
                                <tr>
                                    <th className="ps-4 py-3" style={{ width: '5%' }}>Rank</th>
                                    <th style={{ width: '30%' }}>Page Title</th>
                                    <th style={{ width: '20%' }}>URL Slug</th>
                                    <th style={{ width: '25%' }}>Traffic Share (vs Top Page)</th>
                                    <th className="text-center" style={{ width: '10%' }}>Views</th>
                                    <th className="text-end pe-4" style={{ width: '10%' }}>Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredPages.length === 0 ? (
                                    <tr><td colSpan="6" className="text-center py-5 text-muted">No analytics data found.</td></tr>
                                ) : (
                                    filteredPages.map((page, index) => {
                                        const trafficShare = highestView > 0 ? Math.round((page.views / highestView) * 100) : 0;
                                        
                                        return (
                                            <tr key={page.id}>
                                                <td className="ps-4 fw-bold text-muted">#{index + 1}</td>
                                                <td>
                                                    <div className="fw-bold text-dark">{page.title}</div>
                                                    <small className="text-muted" style={{ fontSize: '11px' }}>Created: {page.createdAt}</small>
                                                </td>
                                                <td><span className="font-monospace text-primary bg-primary bg-opacity-10 px-2 py-1 rounded small">/{page.slug}</span></td>
                                                <td>
                                                    <div className="d-flex align-items-center gap-2">
                                                        <ProgressBar 
                                                            now={trafficShare} 
                                                            variant={index === 0 ? "warning" : "info"} 
                                                            style={{ height: '6px', width: '100%' }} 
                                                        />
                                                        <span className="small text-muted fw-bold" style={{ width: '35px' }}>{trafficShare}%</span>
                                                    </div>
                                                </td>
                                                <td className="text-center">
                                                    <span className="badge bg-dark rounded-pill px-3 py-2 fs-6 fw-normal">
                                                        {page.views.toLocaleString()}
                                                    </span>
                                                </td>
                                                <td className="text-end pe-4">
                                                    <a 
                                                        href={`/${page.slug}`} 
                                                        target="_blank" 
                                                        rel="noopener noreferrer" 
                                                        className="btn btn-sm btn-light border"
                                                        title="Open Page in New Tab"
                                                    >
                                                        <ArrowTopRightOnSquareIcon style={{ width: '14px' }} />
                                                    </a>
                                                </td>
                                            </tr>
                                        );
                                    })
                                )}
                            </tbody>
                        </Table>
                    </div>
                </Card.Body>
            </Card>
        </div>
    );
};

export default LandingPageAnalytics;